import React, { useRef, ReactNode, RefObject } from "react";

import { clamp } from "lodash";
import { useIntl } from "react-intl";

import * as S from "./style";
import Portal from "./Tooltip.portal";

type Placement = {
  x: number;
  y: number;
};

type ContainerPlacementArgs = {
  pointerX: number;
  pointerY: number;
  tooltipWidth: number;
  tooltipHeight: number;
  margin: number;
  containerRect: DOMRect;
  initialY: number;
};

type ViewportPlacementArgs = {
  pointerX: number;
  pointerY: number;
  tooltipWidth: number;
  tooltipHeight: number;
  margin: number;
  viewportWidth: number;
  initialY: number;
};

type PositionType = {
  pointerX: number;
  pointerY: number;
  tooltipElementRect?: DOMRect;
  containerElementRect?: DOMRect;
};

// Calculate placement within a container
function getContainerPlacement({
  pointerX,
  pointerY,
  tooltipWidth,
  tooltipHeight,
  margin,
  containerRect,
  initialY
}: ContainerPlacementArgs): Placement {
  const xIfPlacedLeft = pointerX - tooltipWidth - margin;

  // Default: Place left if it fits within container
  if (xIfPlacedLeft > containerRect.left) {
    return { x: xIfPlacedLeft, y: initialY };
  }

  // Try placing right if left doesn't fit
  const xIfPlacedRight = pointerX + margin;
  if (xIfPlacedRight + tooltipWidth <= containerRect.right) {
    return { x: xIfPlacedRight, y: initialY };
  }

  // Fallback: Place above if neither left nor right fits and ensure it does not
  // go offscreen if the tooltip width can fit in windows width
  const xplacement = pointerX - tooltipWidth / 2;
  const rightLimit = Math.min(xplacement, window.innerWidth - tooltipWidth);
  const leftLimit = 0;
  return {
    x: clamp(xplacement, leftLimit, rightLimit),
    y: pointerY - tooltipHeight
  };
}

// Calculate placement relative to viewport
function getViewportPlacement({
  pointerX,
  pointerY,
  tooltipWidth,
  tooltipHeight,
  margin,
  viewportWidth,
  initialY
}: ViewportPlacementArgs): Placement {
  // Try placing left of the pointer
  const xIfPlacedLeft = pointerX - tooltipWidth - margin;
  if (xIfPlacedLeft > 0) {
    return { x: xIfPlacedLeft, y: initialY };
  }

  // Try placing right of the pointer
  const xIfPlacedRight = pointerX + margin;
  if (xIfPlacedRight + tooltipWidth < viewportWidth) {
    return { x: xIfPlacedRight, y: initialY };
  }

  // Fallback: Place above if neither left nor right fits and ensure it does not
  // go offscreen if the tooltip width can fit in windows width
  const xplacement = pointerX - tooltipWidth / 2;
  const rightLimit = Math.min(xplacement, window.innerWidth - tooltipWidth);
  const leftLimit = 0;
  return {
    x: clamp(xplacement, leftLimit, rightLimit),
    y: pointerY - tooltipHeight - margin
  };
}

// Calculate tooltip position
const calculatePosition = ({
  pointerX,
  pointerY,
  tooltipElementRect,
  containerElementRect
}: PositionType): { transform: string } => {
  const TOOLTIP_MARGIN = 10;
  const DEFAULT_DIMENSIONS = { width: 260, height: 50 };

  const tooltipWidth = tooltipElementRect?.width ?? DEFAULT_DIMENSIONS.width;
  const tooltipHeight = tooltipElementRect?.height ?? DEFAULT_DIMENSIONS.height;
  const initialTooltipY = pointerY - Math.round(tooltipHeight / 3);

  let placementResult: Placement;

  if (containerElementRect) {
    placementResult = getContainerPlacement({
      pointerX,
      pointerY,
      tooltipWidth,
      tooltipHeight,
      margin: TOOLTIP_MARGIN,
      containerRect: containerElementRect,
      initialY: initialTooltipY
    });
  } else {
    const viewportWidth = document.body.offsetWidth;
    placementResult = getViewportPlacement({
      pointerX,
      pointerY,
      tooltipWidth,
      tooltipHeight,
      margin: TOOLTIP_MARGIN,
      viewportWidth,
      initialY: initialTooltipY
    });
  }

  const { x: finalTooltipX, y: finalTooltipY } = placementResult;

  return { transform: `translate(${finalTooltipX}px, ${finalTooltipY}px)` };
};

type TooltipProps = {
  x?: number;
  y?: number;
  isVisible?: boolean;
  forwardedRef?: RefObject<any>;
  maxZoom?: boolean;
  children: ReactNode;
  chartRect?: DOMRect;
  isLeader?: boolean;
};

const Tooltip = ({
  x,
  y,
  isVisible,
  forwardedRef,
  maxZoom = false,
  children,
  chartRect,
  isLeader
}: TooltipProps) => {
  const intl = useIntl();
  const tooltip = useRef<HTMLDivElement>(null);
  const layoutRef = forwardedRef || tooltip;
  const pRef = useRef<HTMLElement>();
  if (!x || !y) return null;

  const transform = calculatePosition({
    pointerX: x,
    pointerY: y,
    tooltipElementRect: layoutRef.current?.getBoundingClientRect(),
    containerElementRect: chartRect
  });

  const visible = !!(isVisible && transform);
  if (!visible) return null;

  return (
    <Portal forwardedRef={pRef}>
      <S.Layout
        isLeader={isLeader}
        isVisible={visible}
        style={transform}
        ref={layoutRef}
      >
        {maxZoom ? (
          <S.Text>
            {intl.formatMessage({
              id: "metrics.info.zoom",
              defaultMessage: "Maximum zoom has been reached"
            })}
          </S.Text>
        ) : (
          children
        )}
      </S.Layout>
    </Portal>
  );
};

export default React.memo(Tooltip);
